import { defineStore } from "pinia";
import axios from "axios";
import { Preferences } from "@capacitor/preferences";

export const useUserStore = defineStore("user", {
  state: () => ({
    user: null,
    token: null,
    refreshToken: null,
    mvCoins: 0,
    direcciones: [],
    isAuthenticated: false,
    isSessionReady: false,
    interceptorSetupDone: false,
  }),



  actions: {
    // Guardar usuario en Pinia y localStorage
    setUser(userData) {
      this.user = userData;
      localStorage.setItem("user", JSON.stringify(userData));
    },

    // Obtener MV Coins
    async obtenerMVCoins() {
      if (!this.user || !this.user.id) return;

      try {
        const response = await axios.get(
          `https://mvexpress.mx/api/get-mvcoins/${this.user.id}`,
          { headers: { Authorization: `Bearer ${this.user.token}` } }
        );
        this.user = { ...this.user, ...response.data };
        this.mvCoins = parseFloat(response.data.mvCoins || 0);
        localStorage.setItem("user", JSON.stringify(this.user));
      } catch (error) {
        console.error("Error al obtener MV Coins:", error);
      }
    },

    // Obtener direcciones
    async obtenerDirecciones() {
      if (!this.user || !this.user.cliente) {
        console.warn("Usuario no logueado, redirigiendo a login...");
        return null;
      }

      const cacheKey = `direcciones_${this.user.cliente}`;
      const cacheTimeKey = `${cacheKey}_timestamp`;
      const now = Date.now();
      const oneWeek = 7 * 24 * 60 * 60 * 1000;

      const cachedData = localStorage.getItem(cacheKey);
      const cachedTime = localStorage.getItem(cacheTimeKey);

      if (cachedData && cachedTime && now - cachedTime < oneWeek) {
        this.direcciones = JSON.parse(cachedData);
        return this.direcciones;
      }

      try {
        const response = await axios.get(
          `https://mvexpress.mx/api/find-direccion/${this.user.cliente}`,
          { headers: { Authorization: `Bearer ${this.user.token}` } }
        );

        this.direcciones = response.data.filter(
          (dir, index, self) =>
            index === self.findIndex((d) => d.CTEDIR_CODIGO_K === dir.CTEDIR_CODIGO_K)
        );

        const jsonData = JSON.stringify(this.direcciones);
        if (jsonData.length < 4 * 1024 * 1024) {
          localStorage.setItem(cacheKey, jsonData);
          localStorage.setItem(cacheTimeKey, now);
        }

        return this.direcciones;
      } catch (error) {
        console.error("Error al obtener direcciones:", error);
        return null;
      }
    },
    async obtenerNuevaData() {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user || !user.id) return;

        const response = await axios.get(`https://mvexpress.mx/api/get-mvcoins/${user.id}`);
        this.user = response.data; // o actualiza userStore.user
      } catch (err) {
        console.error(err);
      }
    },
    async login(email, password) {
      const { data } = await axios.post("https://mvexpress.mx/api/auth", {
        email,
        password,
      });

      this.token = data.token;
      this.refreshToken = data.refreshToken;
      this.isAuthenticated = true;

      // 🕒 LOG: Decodificar y mostrar cuándo expira el token
      try {
        const payload = JSON.parse(atob(data.token.split('.')[1]));
        const expDate = new Date(payload.exp * 1000);
        console.log(`🔑 TU TOKEN EXPIRA EL: ${expDate.toLocaleString()}`);
      } catch (e) { console.warn("No se pudo leer la expiración del token"); }

      this.user = {
        id: data.id,
        cliente: data.cliente,
        email: data.email,
        usuario: data.usuario,
        razon: data.razon,
        rfc: data.rfc,
        telefono: data.telefono,
        mvCoins: data.mvCoins,
        forma_pago: data.forma_pago,
        uso_cfdi: data.uso_cfdi,
        denominacion_comercial: data.denominacion_comercial,
        ruta_preventa: data.ruta_preventa,
        ruta_reparto: data.ruta_reparto,
        politica_privacidad_aceptada: data.politica_privacidad_aceptada,
        fecha_politica_privacidad: data.fecha_politica_privacidad,
      };

      this.direcciones = data.direcciones || [];

      await Preferences.set({
        key: "auth_session",
        value: JSON.stringify({
          token: data.token,
          refreshToken: data.refreshToken,
          user: this.user,
          direcciones: this.direcciones,
        }),
      });

      axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
      this.setupAxiosInterceptors(); // 🛡️ Activar interceptor al loguearse
    },


    async initSession() {
      console.log("🟢 initSession() INICIO");

      if (this.isSessionReady) {
        console.log("⚠️ sesión ya lista, saliendo");
        return;
      }

      const { value } = await Preferences.get({ key: "auth_session" });
      //console.log("📦 auth_session =", value);

      if (!value) {
        console.log("❌ NO hay sesión guardada");
        this.isSessionReady = true;
        return;
      }

      const session = JSON.parse(value);

      if (session && session.token) {
        // ⏰ Revisar expiración del token al iniciar sesión
        try {
          const payload = JSON.parse(atob(session.token.split('.')[1]));
          const expDate = payload.exp * 1000;

          if (Date.now() > expDate) {
            console.warn("⚠️ Token expirado detectado en initSession. Intentando renovar...");
            this.refreshToken = session.refreshToken; // Necesario para refreshAccessToken
            try {
              const newToken = await this.refreshAccessToken();
              session.token = newToken; // Usar el nuevo token
            } catch (e) {
              console.error("❌ Fallo la renovación de token en el inicio, cerrando sesión...");
              await this.logout();
              this.isSessionReady = true;
              if (window.location.pathname !== '/login') {
                window.location.href = "/login";
              }
              return;
            }
          }
        } catch (e) {
          console.warn("⚠️ No se pudo leer la expiración del token", e);
        }

        this.token = session.token;
        this.refreshToken = session.refreshToken;
        this.user = session.user;
        this.direcciones = session.direcciones || [];
        this.isAuthenticated = true;
        console.log("✅ SESIÓN CARGADA CORRECTAMENTE");
        axios.defaults.headers.common.Authorization = `Bearer ${session.token}`;
        this.setupAxiosInterceptors(); // 🛡️ Activar interceptor al cargar sesión
      } else {
        this.isAuthenticated = false;
      }

      this.isSessionReady = true;
    },

    // Acción para actualizar el token tras un refresh exitoso
    async updateToken(newToken) {
      // 🔍 DEBUG: Verificar visualmente que el token cambió
      if (this.token !== newToken) {
        console.log("✅ TOKEN DE ACCESO RENOVADO CORRECTAMENTE");
        console.log("   🔻 Viejo (fin): ..." + (this.token ? this.token.slice(-10) : "Nulo"));
        console.log("   🔹 Nuevo (fin): ..." + (newToken ? newToken.slice(-10) : "Nulo"));
      } else {
        console.warn("⚠️ ALERTA: El backend devolvió exactamente el mismo token. Esto no es normal.");
      }

      this.token = newToken;
      // Actualizar header global de axios por si se usa fuera de api.js
      axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;

      // Actualizar persistencia
      const { value } = await Preferences.get({ key: "auth_session" });
      if (value) {
        const session = JSON.parse(value);
        session.token = newToken;
        await Preferences.set({ key: "auth_session", value: JSON.stringify(session) });
      }
    },

    // 🔄 ACCIÓN: Llamar a tu endpoint de backend para renovar token
    async refreshAccessToken() {
      try {
        if (!this.refreshToken) throw new Error("No hay refresh token disponible");

        console.log("🔄 Intentando renovar token...");

        // Llamada a tu backend (IMPORTANTE: Sin enviar el token viejo/corrupto en el header)
        const { data } = await axios.post("https://mvexpress.mx/api/refresh-token", {
          refreshToken: this.refreshToken
        }, {
          headers: { Authorization: '' }
        });

        console.log("🔄 Token renovado con éxito");
        await this.updateToken(data.token);
        return data.token;
      } catch (error) {
        console.error("❌ Error renovando token:", error);
        await this.logout();
        // 🛑 FIX: Evitar recarga infinita si ya estamos en login
        if (window.location.pathname !== '/login') {
          window.location.href = "/login";
        }
        throw error;
      }
    },

    // 🛡️ INTERCEPTOR: Captura errores 401 y reintenta automáticamente
    setupAxiosInterceptors() {
      if (this.interceptorSetupDone) return;

      axios.interceptors.response.use(
        (response) => response,
        async (error) => {
          const originalRequest = error.config;
          // Si es error 401 (No autorizado) O 403 (Prohibido) y no lo hemos reintentado aún
          if (error.response && (error.response.status === 401 || error.response.status === 403) && !originalRequest._retry) {
            console.warn(`🛡️ Interceptor activado por error ${error.response.status}. Renovando...`);
            originalRequest._retry = true;
            try {
              const newToken = await this.refreshAccessToken();
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest); // Reintentar petición original
            } catch (err) {
              return Promise.reject(err);
            }
          }
          return Promise.reject(error);
        }
      );
      this.interceptorSetupDone = true;
      console.log("🛡️ Sistema de renovación automática de tokens ACTIVADO");
    },


    // Cerrar sesión
    async logout() {
      // Limpiar store
      this.user = null;
      this.token = null;
      this.refreshToken = null;
      this.direcciones = [];
      this.isAuthenticated = false;
      this.isSessionReady = true;

      // 🔥 FUERZA BRUTA: Borra TODAS las claves de Preferences (auth_session, caches, y tus nuevas keys)
      // Esto asegura que no quede basura de ninguna vista.
      await Preferences.clear();

      localStorage.clear();
      sessionStorage.clear();
      localStorage.removeItem("user");
    },

    // ✅ Función para probar token corrupto (Soluciona el error en Home.vue)
    testCorruptToken() {
      this.token = "TOKEN_CORRUPTO_TEST_" + Date.now();
      console.warn("⚠️ Token invalidado manualmente. La próxima petición debería fallar con error 401.");
    }


  },
});
